// Pure amount-mismatch computation for estimate/invoice extraction fields.
// See design Section 6.4.
//
// expected = parts + labour + tax - discount
// isMismatch = |expected - total| > 1  (±₹1 rounding tolerance)
//
// Blank discount defaults to 0. If parts/labour/tax/total is missing or
// non-numeric, the check is skipped entirely (skipped:true) rather than
// reported as a mismatch — an incomplete extraction must never produce a
// false positive.

import type { MismatchResult } from '../types';

export interface AmountFields {
  parts_cost: string;
  labor_charges: string;
  tax_amount: string;
  discount_amount: string;
  total_amount: string;
}

// parseFloat('') is NaN, and parseFloat('not-a-number') is also NaN — both
// correctly signal "missing/non-numeric" for the required fields below.
function parseRequired(value: string): number {
  const n = parseFloat(value);
  return Number.isNaN(n) ? NaN : n;
}

function parseDiscount(value: string): number {
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : n;
}

export function computeMismatch(fields: AmountFields): MismatchResult {
  const parts = parseRequired(fields.parts_cost);
  const labour = parseRequired(fields.labor_charges);
  const tax = parseRequired(fields.tax_amount);
  const discount = parseDiscount(fields.discount_amount);
  const total = parseRequired(fields.total_amount);

  if ([parts, labour, tax, total].some(Number.isNaN)) {
    return { isMismatch: false, skipped: true };
  }

  const expected = parts + labour + tax - discount;
  const isMismatch = Math.abs(expected - total) > 1;
  return { isMismatch, skipped: false, expected, entered: total };
}
